import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button from '@orn/shared-web/components/Button/Button';
import { HookForm, FormField } from '@orn/shared-web/components/Form';
import Input, { PasswordInput } from '@orn/shared-web/components/Input';
import MessagingCard from '@orn/shared-web/components/MessagingCard';
import { CheckIcon } from '@orn/shared-web/icons';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import useAuthApi from '../../../hooks/useAuthApi';
import { OnboardingTitle } from '../../OnboardingLayout';

import type { ILoginResponse } from '../../../hooks/useAuthApi';

interface IPasswordStepProps {
  email: string;
  /**
   * When provided, renders a success banner as the `OnboardingTitle` subtitle
   * — used after a password reset to confirm the new credential is live.
   * Slotting it through the subtitle keeps it within the title's tight
   * 12px-gap group rather than the form column's 32px gap.
   */
  successMessage?: string;
  onNext: (result: ILoginResponse) => void;
  onBack: () => void;
  onForgotPassword: () => void;
  onWaitlisted: () => void;
}

interface IPasswordFormValues {
  password: string;
}

/**
 * Login step that collects the user's password after their email has been
 * confirmed as existing. Displays the email in a read-only input for context,
 * optionally shows a success banner after a password reset, and handles the
 * waitlist and invalid-credential error branches inline.
 */
const PasswordStep = ({
  email,
  successMessage,
  onNext,
  onBack,
  onForgotPassword,
  onWaitlisted,
}: IPasswordStepProps) => {
  const authApi = useAuthApi();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<IPasswordFormValues>({
    defaultValues: { password: '' },
    mode: 'onSubmit',
  });

  const passwordValue = form.watch('password');

  const handleSubmit = React.useCallback(
    async (values: IPasswordFormValues) => {
      setIsSubmitting(true);
      const result = await authApi.login({ email, password: values.password });
      setIsSubmitting(false);

      if (!result.ok) {
        if (result.error.code === ApiErrorCode.AUTH_NOT_ON_ALPHA_ALLOW_LIST) {
          onWaitlisted();
          return;
        }
        form.setError('password', { message: 'Invalid email or password.' });
        return;
      }

      onNext(result.data);
    },
    [authApi, email, form, onNext, onWaitlisted]
  );

  return (
    <>
      <OnboardingTitle
        title="Welcome back"
        onBack={onBack}
        subtitle={
          successMessage && (
            <MessagingCard variant="success" className="w-full">
              <MessagingCard.Text icon={<CheckIcon />}>{successMessage}</MessagingCard.Text>
            </MessagingCard>
          )
        }
      />

      <div className="flex w-full flex-col gap-4">
        <Input label="Email" type="email" value={email} size="md" isDisabled />

        <HookForm form={form} onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex w-full flex-col gap-4">
            <FormField control={form.control} name="password">
              {({ field, fieldState }) => (
                <PasswordInput
                  {...field}
                  autoFocus
                  label="Password"
                  size="md"
                  isInvalid={Boolean(fieldState.error)}
                  hint={fieldState.error?.message}
                  autoComplete="current-password"
                />
              )}
            </FormField>

            <Button
              type="submit"
              size="xl"
              color="quaternary"
              className="w-full"
              isDisabled={!passwordValue}
              isLoading={isSubmitting}
            >
              Sign in
            </Button>
            <Button color="link-gray" size="sm" className="self-center" onClick={onForgotPassword}>
              Forgot password?
            </Button>
          </div>
        </HookForm>
      </div>
    </>
  );
};

PasswordStep.displayName = 'PasswordStep';

export default PasswordStep;
